import { createTheme, ThemeOptions } from "@mui/material/styles";

/**
 * Centralized design tokens.
 *
 * Light mode uses a cool off-white app background with pure-white surfaces so
 * cards read as raised without needing heavy shadows. Dark mode uses deep
 * charcoal (never pure black) and slightly desaturated accents to keep WCAG
 * contrast comfortable and reduce eye strain. Semantic colors are muted away
 * from the default neon MUI tones.
 */
const lightColors = {
  primary: "#274c77",
  secondary: "#f8412a",
  background: "#f7f9fc", // cool light gray app canvas
  white: "#ffffff", // card / paper surface
  border: "rgba(39, 76, 119, 0.10)",
  inputFill: "rgba(39, 76, 119, 0.03)",
  gradient: {
    start: "#ff705e",
    end: "#f8412a",
  },
  text: {
    primary: "#274c77",
    secondary: "rgba(39, 76, 119, 0.7)",
  },
  semantic: {
    success: "#2e7d58",
    error: "#d05151",
    warning: "#d19a3a",
    info: "#3a7bbf",
  },
};

const darkColors = {
  primary: "#64b5f6",
  secondary: "#ff9800",
  background: "#121212", // deep charcoal canvas (not pure black)
  white: "#1e1e1e", // elevated surface
  border: "rgba(255, 255, 255, 0.08)",
  inputFill: "rgba(255, 255, 255, 0.04)",
  gradient: {
    start: "#ff9800",
    end: "#ff6f00",
  },
  text: {
    primary: "#e0e0e0",
    secondary: "rgba(224, 224, 224, 0.7)",
  },
  semantic: {
    success: "#66bb8a",
    error: "#e57373",
    warning: "#e0b25f",
    info: "#64b5f6",
  },
};

// Shared radius scale. 10px reads modern without looking like a toy.
const RADIUS = 10;

const createAppTheme = (isDark: boolean) => {
  const colors = isDark ? darkColors : lightColors;

  // Soft, diffuse shadow used by surface overrides. Kept subtle so depth comes
  // from borders + background contrast rather than hard drop shadows.
  const softShadow = isDark
    ? "0 1px 2px rgba(0,0,0,0.4), 0 4px 16px rgba(0,0,0,0.35)"
    : "0 1px 2px rgba(39,76,119,0.04), 0 8px 24px rgba(39,76,119,0.06)";

  const themeOptions: ThemeOptions = {
    shape: {
      borderRadius: RADIUS,
    },
    palette: {
      mode: isDark ? "dark" : "light",
      primary: {
        main: colors.primary,
      },
      secondary: {
        main: colors.secondary,
      },
      background: {
        default: colors.background,
        paper: colors.white,
      },
      text: {
        primary: colors.text.primary,
        secondary: colors.text.secondary,
      },
      success: { main: colors.semantic.success },
      error: { main: colors.semantic.error },
      warning: { main: colors.semantic.warning },
      info: { main: colors.semantic.info },
      divider: colors.border,
    },
    typography: {
      fontFamily: ["Poppins", "Roboto", "sans-serif"].join(","),
      button: {
        // modern, non-shouty buttons
        textTransform: "none",
        fontWeight: 600,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          "*": {
            // Custom scrollbar for all elements (Webkit browsers)
            "&::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: isDark
                ? "rgba(255, 255, 255, 0.3)"
                : "rgba(0, 0, 0, 0.3)",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: isDark
                ? "rgba(255, 255, 255, 0.5)"
                : "rgba(0, 0, 0, 0.5)",
            },
            // Firefox scrollbar styling
            scrollbarWidth: "thin",
            scrollbarColor: isDark
              ? "rgba(255, 255, 255, 0.3) transparent"
              : "rgba(0, 0, 0, 0.3) transparent",
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            padding: "16px 24px 0px 24px",
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: "0 24px 20px 24px",
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: RADIUS + 2,
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            borderRadius: RADIUS,
            paddingInline: "18px",
            "&.Mui-disabled": {
              background: "rgba(0, 0, 0, 0.12)",
              color: "rgba(0, 0, 0, 0.26)",
            },
          },
          contained: {
            background: `linear-gradient(316deg, ${colors.gradient.start}, ${colors.gradient.end})`,
            color: "#ffffff",
            boxShadow: "none",
            "&:hover": {
              background: `linear-gradient(316deg, ${colors.gradient.start}, ${colors.gradient.end})`,
              opacity: 0.9,
              boxShadow: "none",
            },
          },
          text: {
            color: colors.text.primary,
            "&:hover": {
              backgroundColor: isDark
                ? "rgba(224, 224, 224, 0.08)"
                : "rgba(39, 76, 119, 0.04)",
            },
          },
        },
      },
      // Surfaces: lean on borders + a soft shadow instead of hard elevations.
      // Components that explicitly opt into an `elevation` prop still get MUI's
      // elevation applied, so existing elevation={n} usages are preserved.
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: RADIUS,
            border: `1px solid ${colors.border}`,
            boxShadow: softShadow,
            backgroundImage: "none",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          rounded: {
            borderRadius: RADIUS,
          },
          // Flatten the default surface tint in dark mode so papers don't get
          // progressively lighter with elevation.
          root: {
            backgroundImage: "none",
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: RADIUS,
            backgroundColor: colors.inputFill,
            transition: "border-color 120ms ease, box-shadow 120ms ease",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: colors.border,
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: isDark
                ? "rgba(255,255,255,0.2)"
                : "rgba(39,76,119,0.25)",
            },
            // Sleek offset focus ring rather than a plain thick border.
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: colors.primary,
              borderWidth: 1,
            },
            "&.Mui-focused": {
              boxShadow: `0 0 0 3px ${
                isDark ? "rgba(100,181,246,0.25)" : "rgba(39,76,119,0.15)"
              }`,
            },
          },
        },
      },
      MuiAppBar: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundColor: colors.white,
            color: colors.text.primary,
            borderBottom: `1px solid ${colors.border}`,
            boxShadow: "none",
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "#f5f5f5",
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "#f5f5f5",
            color: colors.text.primary,
            fontWeight: 600,
          },
        },
      },
    },
  };

  return createTheme(themeOptions);
};

export const lightTheme = createAppTheme(false);
export const darkTheme = createAppTheme(true);
export const theme = lightTheme;
